/**
 * Super Admin → Tenants card: how many admins this tenant may have active at once (its Admin → Team page).
 * The server is the only place the limit is enforced; this just reads and sets it.
 */
import { useEffect, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../lib/api";

const PRESETS = [1, 3, 5, 10];

export default function AdminLimitControl({ tenant }) {
    const [info, setInfo] = useState(null);
    const [value, setValue] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let alive = true;
        api.get(`/super/tenants/${tenant.id}/admin-limit`)
            .then(({ data }) => { if (alive) { setInfo(data); setValue(String(data.limit)); } })
            .catch(() => { if (alive) setInfo({ error: true }); });
        return () => { alive = false; };
    }, [tenant.id]);

    const save = async (limit) => {
        setBusy(true);
        try {
            const { data } = await api.patch(`/super/tenants/${tenant.id}/admin-limit`, { limit });
            setInfo(data);
            setValue(String(data.limit));
            toast.success(`${tenant.name}: up to ${data.limit} active admin${data.limit === 1 ? "" : "s"}${data.is_default ? " (default)" : ""}`);
            if (data.over_limit) {
                toast.warning(`${tenant.name} already has ${data.active} active admins. They keep working, but no admin can be added or activated until it's under ${data.limit}.`, { duration: 8000 });
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save the admin limit.");
            if (info && !info.error) setValue(String(info.limit));
        } finally {
            setBusy(false);
        }
    };

    const typed = Number(value);
    const valid = Number.isInteger(typed) && typed >= 1 && typed <= 100;

    return (
        <div className="mt-2 text-xs" data-testid={`admin-limit-${tenant.slug}`}>
            <div className="flex flex-wrap items-center gap-2">
                <ShieldCheck size={13} className="text-muted-foreground" />
                <span className="font-semibold">Active admins</span>
                {!info ? (
                    <Loader2 size={12} className="animate-spin text-muted-foreground" />
                ) : info.error ? (
                    <span className="text-destructive">Couldn't load</span>
                ) : (
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${info.over_limit ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" : "bg-muted text-muted-foreground"}`} data-testid={`admin-limit-usage-${tenant.slug}`}>
                        {info.active} / {info.limit}{info.is_default ? " · default" : ""}
                    </span>
                )}
            </div>
            {info && !info.error && (
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5 ml-[21px]">
                    {PRESETS.map((n) => (
                        <button
                            key={n}
                            type="button"
                            data-testid={`admin-limit-preset-${tenant.slug}-${n}`}
                            disabled={busy || n === info.limit}
                            onClick={() => save(n)}
                            className={`min-w-[2.25rem] px-2 py-1 rounded-md border text-[11px] font-semibold ${n === info.limit ? "border-primary bg-primary/10 text-primary" : "border-border bg-muted/40 hover:bg-muted"}`}
                        >
                            {n}
                        </button>
                    ))}
                    <input
                        type="number"
                        min={1}
                        max={100}
                        inputMode="numeric"
                        aria-label="Maximum active admins"
                        data-testid={`admin-limit-input-${tenant.slug}`}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        className="w-16 bg-muted rounded-md px-2 py-1 text-[11px] font-semibold border border-border"
                    />
                    <button
                        type="button"
                        data-testid={`admin-limit-save-${tenant.slug}`}
                        disabled={busy || !valid || typed === info.limit}
                        onClick={() => save(typed)}
                        className="px-2 py-1 rounded-md bg-primary text-primary-foreground text-[11px] font-bold disabled:opacity-50"
                    >
                        Set
                    </button>
                    {!info.is_default && (
                        <button
                            type="button"
                            data-testid={`admin-limit-reset-${tenant.slug}`}
                            disabled={busy}
                            onClick={() => save(null)}
                            className="px-2 py-1 rounded-md text-[11px] font-semibold text-muted-foreground underline"
                        >
                            Use default ({info.default_limit})
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
